/**
 * script-adherence — «Соответствие скрипту»: имя и версия скрипта, общий
 * балл, число выполненных шагов и шагов в правильном порядке, пропущенные
 * ОБЯЗАТЕЛЬНЫЕ формулировки. Данные приходят из отдельного поля
 * `input.scriptAdherence` (не из `analysis.result`, как остальные секции) —
 * загрузка script-adherence на экране тоже отдельный API-вызов, ScriptAdherenceTab
 * получает свои данные пропом `adherence`, отдельно от `XMLAnalysisResult`.
 *
 * Числа «выполнено/всего» и «в порядке/всего» — сырой слэш, как в
 * стат-плитках ScriptAdherenceTab.tsx (`{completed}/{total}`), а не
 * локализованная фраза «N из M»: под такую фразу нет i18n-ключа, а экран сам
 * обходится числами без слов.
 *
 * «В правильном порядке» делится на `stepsTotal`, а НЕ на `stepsCompleted` —
 * ровно как стат-плитка «In Order» на экране. Знаменатель — шаги, которые
 * были ДОЛЖНЫ (#701): необязательный шаг, который на этом разговоре не
 * случился, не входит ни в балл, ни в счётчик, потому что редактор обещает,
 * что он ничего не стоит. Согласованный прототип (preview.html:241) в
 * иллюстративном примере даёт «4 из 5» — то есть визуально предполагает
 * знаменателем 5 (stepsCompleted), но это внутренне не сходится с реальными
 * полями ScriptAdherenceData; прототип авторитетен для вёрстки kv-строки, не
 * для того, что в неё подставлено — экран здесь источник истины по поведению.
 *
 * Пропущенные формулировки — только `isRequired: true`, зеркалит блок
 * «Missed Required Phrases» экрана (`missedPhrases.filter(p => p.isRequired)`).
 * Необязательных пропусков в данных больше нет вовсе: незакрытый бонус не
 * стоит баллов, поэтому нигде не записывается как пробел (#701).
 *
 * `orderScore` (третья стат-плитка экрана, % совпадения порядка) бриф не
 * упоминает, и согласованный прототип для этой карточки его тоже не
 * показывает — сознательно не печатается.
 */
import { card, kvRow, scoreSpan } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';

export const renderScriptAdherence: SectionFn = (input, t) => {
  const adherence = input.scriptAdherence;
  if (!adherence) return '';

  const scriptHtml = adherence.scriptName
    ? kvRow(
        t('scripts.script'),
        escapeHtml(adherence.scriptName) +
          (adherence.scriptVersion > 1 ? ` v${adherence.scriptVersion}` : '')
      )
    : '';

  const stepsHtml = kvRow(
    t('scripts.stepsCompleted'),
    `${adherence.stepsCompleted}/${adherence.stepsTotal}`
  );

  const inOrderHtml = kvRow(
    t('scripts.stepsInOrder'),
    `${adherence.stepsInOrder}/${adherence.stepsTotal}`
  );

  const missedRequired = adherence.missedPhrases.filter((p) => p.isRequired);
  const missedHtml =
    missedRequired.length > 0
      ? `<div class="blk" style="margin-top:10px"><div class="lbl">${escapeHtml(
          t('scripts.missedRequiredPhrases')
        )}</div>${missedRequired
          .map(
            (p) =>
              `<div class="chk"><span class="i no">✗</span><span>${escapeHtml(p.phraseText)}</span></div>`
          )
          .join('')}</div>`
      : '';

  return card({
    title: t('records.analysisPanel.scriptAdherence'),
    value: scoreSpan(adherence.adherenceScore),
    body: scriptHtml + stepsHtml + inOrderHtml + missedHtml,
  });
};
